import { motion } from "framer-motion";
import {
  CheckCircle,
  XCircle,
  ArrowRight,
  Calculator,
  Building,
  Briefcase,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const services = [
  {
    title: "Tax Return Services",
    icon: <Calculator className="w-10 h-10 text-primary" />,
    description:
      "Expert preparation and lodgment of tax returns for individuals and businesses",
    image:
      "https://images.unsplash.com/photo-1554224155-6726b3ff858f?q=100&w=2000&auto=format&fit=crop&ixlib=rb-4.0.3",
    features: [
      "Maximized deductions and credits",
      "Compliance with latest tax laws",
      "Electronic lodgment with the ATO",
      "Year-round tax planning advice",
      "Prior year amendments if needed",
    ],
  },
  {
    title: "Business Registration",
    icon: <Building className="w-10 h-10 text-primary" />,
    description:
      "Comprehensive business registration services including ABN, GST, and company setup",
    image:
      "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=100&w=2000&auto=format&fit=crop&ixlib=rb-4.0.3",
    features: [
      "ABN and business name registration",
      "GST registration and advice",
      "Company formation and ASIC compliance",
      "Business structure optimization",
      "Ongoing registration maintenance",
    ],
  },
  {
    title: "Financial Advisory",
    icon: <Briefcase className="w-10 h-10 text-primary" />,
    description:
      "Strategic financial planning and advisory services for growth and stability",
    image:
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=2015&auto=format&fit=crop&ixlib=rb-4.0.3",
    features: [
      "Personalized financial strategies",
      "Investment and retirement planning",
      "Cash flow management",
      "Debt reduction strategies",
      "Wealth creation and protection",
    ],
  },
];

const comparisonPoints = [
  {
    title: "Expert Knowledge",
    withUs: true,
    withoutUs: false,
    description:
      "Access to certified professionals with deep industry knowledge",
  },
  {
    title: "Time Savings",
    withUs: true,
    withoutUs: false,
    description:
      "Focus on your business while we handle the financial complexities",
  },
  {
    title: "Compliance Assurance",
    withUs: true,
    withoutUs: false,
    description: "Stay compliant with all regulatory requirements",
  },
  {
    title: "Strategic Planning",
    withUs: true,
    withoutUs: false,
    description: "Proactive approach to financial optimization",
  },
  {
    title: "Ongoing Support",
    withUs: true,
    withoutUs: false,
    description: "Year-round assistance and advisory services",
  },
];

const ServicesComparison = () => {
  return (
    <section className="py-24 bg-gradient-to-b from-muted/30 to-background dark:from-gray-900/30 dark:to-gray-950">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-4xl font-bold mb-5 text-foreground dark:text-white">
              Our Professional Services
            </h2>
            <div className="w-20 h-1 bg-primary mx-auto mb-5"></div>
            <p className="text-muted-foreground dark:text-gray-300 max-w-2xl mx-auto text-lg">
              Comprehensive financial solutions tailored to your needs
            </p>
          </motion.div>
        </div>

        {/* Services Showcase */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-card dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg border border-border dark:border-gray-700 hover:shadow-xl transition-all duration-300 hover:border-primary/20 flex flex-col"
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end">
                  <div className="p-6">
                    <h3 className="text-white text-xl font-bold">
                      {service.title}
                    </h3>
                  </div>
                </div>
              </div>

              <div className="p-6 flex-grow">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center">
                    {service.icon}
                  </div>
                  <p className="text-muted-foreground dark:text-gray-300">
                    {service.description}
                  </p>
                </div>

                <ul className="space-y-2 mt-4">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-foreground dark:text-white">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-6 pt-0 mt-auto">
                <Button className="w-full" variant="outline" asChild>
                  {service.title === "Tax Return Services" ? (
                    <a href="/tax-information">
                      Learn More <ArrowRight className="ml-2 h-4 w-4" />
                    </a>
                  ) : service.title === "Business Registration" ? (
                    <a href="/company-registration">
                      Learn More <ArrowRight className="ml-2 h-4 w-4" />
                    </a>
                  ) : (
                    <a href="/contact">
                      Learn More <ArrowRight className="ml-2 h-4 w-4" />
                    </a>
                  )}
                </Button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Comparison Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-card dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg border border-border dark:border-gray-700 mb-16"
        >
          <div className="p-8 text-center border-b border-border dark:border-gray-700">
            <h3 className="text-2xl font-bold text-foreground dark:text-white mb-2">
              Why Choose Our Services?
            </h3>
            <p className="text-muted-foreground dark:text-gray-300">
              See how our professional services compare to handling things on
              your own
            </p>
          </div>

          <div className="grid grid-cols-3 text-center py-4 bg-muted/50 dark:bg-gray-800/50 border-b border-border dark:border-gray-700">
            <div className="px-4 font-semibold text-foreground dark:text-white">
              Feature
            </div>
            <div className="px-4 font-semibold text-primary">
              With Numberz Insight
            </div>
            <div className="px-4 font-semibold text-muted-foreground dark:text-gray-400">
              On Your Own
            </div>
          </div>

          {comparisonPoints.map((point, index) => (
            <div
              key={index}
              className={cn(
                "grid grid-cols-3 py-4 px-4 text-center",
                index % 2 === 0
                  ? "bg-background dark:bg-gray-900/30"
                  : "bg-muted/30 dark:bg-gray-800/30"
              )}
            >
              <div className="flex flex-col justify-center">
                <span className="text-foreground dark:text-white font-medium">
                  {point.title}
                </span>
                <span className="text-sm text-muted-foreground dark:text-gray-400 hidden md:block">
                  {point.description}
                </span>
              </div>
              <div className="flex items-center justify-center">
                {point.withUs ? (
                  <CheckCircle className="h-6 w-6 text-green-500 dark:text-green-400" />
                ) : (
                  <XCircle className="h-6 w-6 text-red-500 dark:text-red-400" />
                )}
              </div>
              <div className="flex items-center justify-center">
                {point.withoutUs ? (
                  <CheckCircle className="h-6 w-6 text-green-500 dark:text-green-400" />
                ) : (
                  <XCircle className="h-6 w-6 text-red-500 dark:text-red-400" />
                )}
              </div>
            </div>
          ))}

          <div className="p-8 text-center bg-muted/10 dark:bg-gray-800/50 border-t border-border dark:border-gray-700">
            <Button
              size="lg"
              onClick={() => (window.location.href = "/contact")}
            >
              Get Started Today <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </motion.div>

        {/* Final CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-center"
        >
          <p className="text-muted-foreground dark:text-gray-300 mb-4">
            Need more information about our services?
          </p>
          <Button variant="outline" asChild>
            <a href="/contact">
              Contact Our Team <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesComparison;
